import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { SignInButton, SignUpButton, SignedIn, SignedOut, UserButton, useUser } from '@clerk/clerk-react';
import { setCurrentPage } from '../../store/slices/uiSlice';
import { Menu, X, Zap, User, Bell } from 'lucide-react';

const Header = () => {
  const dispatch = useDispatch();
  const { user, isSignedIn } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(0);
  
  const { currentPage } = useSelector((state) => state.ui);
  const subscription = useSelector((state) => state.subscription);

  // Mock current streak - in real app this would come from your backend
  useEffect(() => {
    // This would typically be fetched from your backend/Redux store
    setCurrentStreak(Math.floor(Math.random() * 50) + 1);
  }, []);

  const navigationItems = [
    { page: 'dashboard', label: 'Dashboard', public: false },
    { page: 'settings', label: 'Settings', public: false },
  ];

  const handleNavigation = (page) => {
    dispatch(setCurrentPage(page));
    setIsOpen(false);
  };

  const isActive = (page) => {
    return currentPage === page;
  };

  const planName = subscription?.plan_type === 'yearly' ? 'Yearly Plan' : 'Monthly Plan';

  return (
    <>
      <header className="bg-white/90 backdrop-blur-md border-b border-gray-200/20 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Enhanced Logo */}
            <button 
              onClick={() => handleNavigation('dashboard')}
              className="flex items-center space-x-3 group"
            >
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-200">
                  <Zap className="w-6 h-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full animate-pulse"></div>
              </div>
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  StreakKeeper
                </span>
                {isSignedIn && currentStreak > 0 && (
                  <div className="text-xs text-gray-500 -mt-1">
                    {currentStreak} day streak 🔥
                  </div>
                )}
              </div>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              <SignedIn>
                {navigationItems.filter(item => !item.public).map((item) => {
                  return (
                    <button
                      key={item.page}
                      onClick={() => handleNavigation(item.page)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive(item.page) 
                          ? 'text-blue-600 bg-blue-50 shadow-sm' 
                          : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </SignedIn>
              
              {/* Public navigation items (visible to everyone) */}
              {navigationItems.filter(item => item.public).map((item) => {
                return (
                  <button
                    key={item.page}
                    onClick={() => handleNavigation(item.page)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive(item.page) 
                        ? 'text-blue-600 bg-blue-50 shadow-sm' 
                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            {/* Desktop User Menu / Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              <SignedIn>
                <div className="flex items-center space-x-4">
                  {/* Notification Bell */}
                  <button className="relative p-2 text-gray-600 hover:text-blue-600 transition-colors">
                    <Bell className="w-5 h-5" />
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
                  </button>

                  {/* Plan Badge */}
                  {subscription?.has_access && (
                    <div className="flex items-center space-x-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg px-3 py-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-xs font-medium text-green-700">{planName}</span>
                    </div>
                  )}

                  {/* User Avatar using Clerk's UserButton */}
                  <UserButton 
                    appearance={{
                      elements: {
                        avatarBox: "w-8 h-8",
                        userButtonPopoverCard: "bg-white shadow-xl border border-gray-200 rounded-xl",
                        userButtonPopoverActions: "flex flex-col space-y-1"
                      }
                    }}
                    userProfileProps={{
                      appearance: {
                        elements: {
                          card: "shadow-xl"
                        }
                      }
                    }}
                  />
                </div>
              </SignedIn>
              
              <SignedOut>
                <div className="flex items-center space-x-3">
                  <SignInButton mode="modal">
                    <button className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                      Sign In
                    </button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25">
                      Get Started
                    </button>
                  </SignUpButton>
                </div>
              </SignedOut>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button onClick={() => setIsOpen(!isOpen)} className="p-2 rounded-lg text-gray-700 hover:bg-gray-100">
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-3 space-y-1">
              <SignedIn>
                {navigationItems.filter(item => !item.public).map((item) => (
                  <button
                    key={item.page}
                    onClick={() => handleNavigation(item.page)}
                    className={`block w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive(item.page) 
                        ? 'text-blue-600 bg-blue-50 shadow-sm' 
                        : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </SignedIn>

              {/* Public navigation items (visible to everyone) */}
              {navigationItems.filter(item => item.public).map((item) => (
                <button
                  key={item.page}
                  onClick={() => handleNavigation(item.page)}
                  className={`block w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(item.page) 
                      ? 'text-blue-600 bg-blue-50 shadow-sm' 
                      : 'text-gray-700 hover:text-blue-600 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}

              {/* Auth Buttons for mobile */}
              <SignedOut>
                <div className="flex items-center space-x-3 mt-2">
                  <SignInButton mode="modal">
                    <button className="flex-1 text-gray-700 hover:text-blue-600 font-medium transition-colors">
                      Sign In
                    </button>
                  </SignInButton>
                  <SignUpButton mode="modal">
                    <button className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25">
                      Get Started
                    </button>
                  </SignUpButton>
                </div>
              </SignedOut>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;